import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { StatusBadge } from "@/components/StatusBadge";
import { Check, X } from "lucide-react";

export default function Users() {
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/users")).data, refetchInterval: 15000 });

  const approve = useMutation({
    mutationFn: async (id) => (await api.post(`/users/${id}/approve`)).data,
    onSuccess: () => { toast.success("User approved"); qc.invalidateQueries({ queryKey: ["users"] }); },
    onError: () => toast.error("Failed"),
  });
  const reject = useMutation({
    mutationFn: async (id) => (await api.post(`/users/${id}/reject`)).data,
    onSuccess: () => { toast.success("User rejected"); qc.invalidateQueries({ queryKey: ["users"] }); },
    onError: () => toast.error("Failed"),
  });

  return (
    <div className="animate-fade-in">
      <header className="h-14 border-b border-zinc-800 flex items-center px-6">
        <h1 className="font-heading text-lg font-semibold tracking-tight">Users</h1>
        <span className="ml-3 text-xs font-mono text-zinc-500">admin approval workflow</span>
      </header>
      <div className="p-6">
        <div className="border border-zinc-800 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-zinc-950 text-[10px] uppercase tracking-wider text-zinc-500">
                <th className="text-left font-medium py-2 px-3">Name</th>
                <th className="text-left font-medium py-2 px-3">Email</th>
                <th className="text-left font-medium py-2 px-3">Role</th>
                <th className="text-left font-medium py-2 px-3">Status</th>
                <th className="text-left font-medium py-2 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-zinc-800/60 hover:bg-zinc-900 transition-colors duration-150">
                  <td className="py-2 px-3 text-zinc-200">{u.name}</td>
                  <td className="py-2 px-3 font-mono text-zinc-400 text-xs">{u.email}</td>
                  <td className="py-2 px-3 font-mono text-zinc-300 text-xs">{u.role}</td>
                  <td className="py-2 px-3"><StatusBadge status={u.status} /></td>
                  <td className="py-2 px-3">
                    {u.status === "pending" ? (
                      <div className="flex gap-2">
                        <button data-testid={`approve-user-${u.id}`} onClick={() => approve.mutate(u.id)}
                          className="flex items-center gap-1 text-xs px-2 py-1 border border-emerald-700 text-emerald-400 rounded-sm hover:bg-emerald-900/30"><Check className="h-3 w-3" /> Approve</button>
                        <button data-testid={`reject-user-${u.id}`} onClick={() => reject.mutate(u.id)}
                          className="flex items-center gap-1 text-xs px-2 py-1 border border-red-700 text-red-400 rounded-sm hover:bg-red-900/30"><X className="h-3 w-3" /> Reject</button>
                      </div>
                    ) : <span className="text-xs text-zinc-600">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
